import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { ChevronDown, ChevronRight, Tag } from "lucide-react";
import { useMemo, useState } from "react";

import type { Deal } from "@cohesive/api-entities";
import { Button } from "../../ui/button";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardProps,
  BaseEntityCardTitle,
} from "../BaseCard";

export { Deal };

type DealCardProps = Deal &
  BaseEntityCardProps & {
    custom_properties?: Record<string, string>;
  };

// Avatar component for DealCard
export function DealCardAvatar({
  platform,
  className,
  style,
}: Partial<DealCardProps>) {
  return (
    <BaseEntityCardIcon
      className={cn("bg-transparent", className)}
      style={style}
    >
      <img
        src={`${import.meta.env.VITE_STATIC_BASE_URL || ""}/images/integrations/logos/${platform}.svg`}
        alt={platform}
        className="h-full w-full"
      />
    </BaseEntityCardIcon>
  );
}

export function DealCard({
  platform,
  name,
  amount,
  pipeline,
  stage,
  closed_at,
  created_at,
  web_link,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
  custom_properties,
}: DealCardProps) {
  const [showCustomProperties, setShowCustomProperties] = useState(false);

  const hasCustomProperties = useMemo(() => {
    return custom_properties && Object.keys(custom_properties).length > 0;
  }, [custom_properties]);

  const formatPropertyName = (propName: string) => {
    return propName.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <DealCardAvatar platform={platform} />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle>{name}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {created_at &&
                formatDistanceToNow(new Date(created_at), {
                  addSuffix: true,
                })}
            </span>
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {web_link && <BaseEntityCardExternalLinkAction href={web_link} />}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          <BaseEntityCardDescription className="text-xs text-muted-foreground">
            {amount && (
              <span className="font-medium text-foreground">
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(Number(amount))}
              </span>
            )}
            {pipeline && stage && (
              <span className="ml-2">
                {pipeline} • {stage}
              </span>
            )}
            {closed_at && (
              <span className="ml-2">
                Closed {formatDistanceToNow(new Date(closed_at))} ago
              </span>
            )}
          </BaseEntityCardDescription>

          {/* Collapsible Custom Properties Section */}
          {hasCustomProperties && (
            <div className="flex flex-col gap-1.5 mt-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-5 p-0 text-xs text-muted-foreground hover:text-foreground justify-start -ml-1"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCustomProperties(!showCustomProperties);
                }}
              >
                {showCustomProperties ? (
                  <ChevronDown className="h-3 w-3 mr-1" />
                ) : (
                  <ChevronRight className="h-3 w-3 mr-1" />
                )}
                <Tag className="h-3 w-3 mr-1" />
                {Object.keys(custom_properties!).length} custom{" "}
                {Object.keys(custom_properties!).length === 1
                  ? "property"
                  : "properties"}
              </Button>

              {showCustomProperties && (
                <div className="mt-1 space-y-1 pl-1">
                  {Object.entries(custom_properties!).map(([key, value]) => (
                    <div key={key} className="flex items-start gap-2 text-xs">
                      <span className="text-muted-foreground font-medium min-w-0 truncate">
                        {formatPropertyName(key)}:
                      </span>
                      <span className="text-foreground break-words min-w-0">
                        {value || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
